// File: DashboardGraph.tsx
// Orders overview chart (Chart.js): value series (kr) are bars on the left axis,
// quantity series (st) are lines with dots on the right axis. Which series are shown
// is picked with chips + presets on the card itself (replaces the "Information" header filter).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import type { ChartConfiguration, ChartDataset, ChartOptions, Scale } from 'chart.js';

import { fmtInt, fmtMoney, getDisplayLabels, getRollingDashboardLabels, sumArray } from './DashboardHelpers';
import { DashboardPalette, useDashboardChartTheme, useDashboardColors } from './DashboardColors';
import { DashboardCard } from './DashboardLayout';
import type { Period, SalesChannel, SalesRep } from 'src/pages/dashboard/DashboardFilters';

Chart.register(BarController, BarElement, CategoryScale, LineController, LineElement, LinearScale, PointElement, ChartTooltip);

/* ----------------------------------------------------------------------------
 * Metric definitions (same keys as DashboardBarKey / the old Information filter)
 * ---------------------------------------------------------------------------*/

export type DashboardMetricUnit = 'kr' | 'st';
export type DashboardMetricGroup = 'Sales' | 'Warehouse' | 'Finance';
type ChannelColors = { store: string; online: string };

export type DashboardMetricDef = {
  key: DashboardBarKey;
  label: string; // i18n key (same keys as the old Information filter)
  unit: DashboardMetricUnit;
  group: DashboardMetricGroup;
  /** true = drawn per sales channel (Store dark shade, Online light shade) */
  split: boolean;
  /** [store bucket, online bucket] from ChartDataProvider.getStandardBuckets */
  buckets: [StandardSeriesKeys, StandardSeriesKeys];
  color: (palette: DashboardPalette) => ChannelColors;
};

const same = (c: string): ChannelColors => ({ store: c, online: c });

export const DASHBOARD_METRICS: DashboardMetricDef[] = [
  { key: 'ordersBar', label: 'Orders (qty)', unit: 'st', group: 'Sales', split: true, buckets: ['ordersStore', 'ordersOnline'], color: p => p.orders },
  { key: 'ordersValueBar', label: 'Orders (value)', unit: 'kr', group: 'Sales', split: true, buckets: ['ordersValueStore', 'ordersValueOnline'], color: p => p.orderValue },
  { key: 'toShipBar', label: 'To Ship', unit: 'st', group: 'Warehouse', split: false, buckets: ['toShipStore', 'toShipOnline'], color: p => same(p.toShip) },
  { key: 'shippedBar', label: 'Shipped', unit: 'st', group: 'Warehouse', split: false, buckets: ['shippedStore', 'shippedOnline'], color: p => same(p.shipped) },
  { key: 'returnsBar', label: 'Returns (qty)', unit: 'st', group: 'Warehouse', split: false, buckets: ['returnsStore', 'returnsOnline'], color: p => same(p.returns) },
  { key: 'purchasesValueBar', label: 'Purchases (value)', unit: 'kr', group: 'Finance', split: false, buckets: ['purchasesValueStore', 'purchasesValueOnline'], color: p => same(p.purchasing) },
  { key: 'creditNotesBar', label: 'Credit Notes (qty)', unit: 'st', group: 'Finance', split: false, buckets: ['creditNotesStore', 'creditNotesOnline'], color: p => same(p.credits) },
  { key: 'creditNotesValueBar', label: 'Credit Notes (value)', unit: 'kr', group: 'Finance', split: false, buckets: ['creditNotesValueStore', 'creditNotesValueOnline'], color: p => same(p.creditsValue) },
];

const METRIC_GROUPS: DashboardMetricGroup[] = ['Sales', 'Warehouse', 'Finance'];

export type DashboardMetricPreset = 'Sales' | 'Warehouse' | 'Finance' | 'All';
export const DASHBOARD_METRIC_PRESETS: Record<DashboardMetricPreset, DashboardBarKey[]> = {
  Sales: ['ordersBar', 'ordersValueBar'],
  Warehouse: ['toShipBar', 'shippedBar', 'returnsBar'],
  Finance: ['purchasesValueBar', 'creditNotesBar', 'creditNotesValueBar'],
  All: DASHBOARD_METRICS.map(m => m.key),
};

const fmtUnit = (value: number, unit: DashboardMetricUnit) => (unit === 'kr' ? fmtMoney(value) : `${fmtInt(value)} st`);
const fmtAxis = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${+(n / 1e3).toFixed(a >= 1e4 ? 0 : 1)}k`;
  return `${+n.toFixed(1)}`;
};
const addArrays = (a: number[] = [], b: number[] = []) => a.map((v, i) => (v || 0) + (b[i] || 0));

/* ----------------------------------------------------------------------------
 * Series building
 * ---------------------------------------------------------------------------*/

type DashSeries = {
  id: string;
  metric: DashboardMetricDef;
  name: string;
  unit: DashboardMetricUnit;
  color: string;
  data: number[];
  stack: string | null;
};

const buildSeries = (
  selectedBars: DashboardBarKey[],
  buckets: Record<StandardSeriesKeys, number[]>,
  channels: SalesChannel[],
  palette: DashboardPalette,
  t: (key: string) => string,
): DashSeries[] => {
  const out: DashSeries[] = [];
  DASHBOARD_METRICS.forEach(metric => {
    if (!selectedBars.includes(metric.key)) return;
    const [storeKey, onlineKey] = metric.buckets;
    const colors = metric.color(palette);
    if (metric.split) {
      // Store first so it sits at the bottom of the stack (dark shade), Online on top (light shade)
      if (channels.includes('Store')) out.push({ id: `${metric.key}-Store`, metric, name: `${t(metric.label)} · ${t('Store')}`, unit: metric.unit, color: colors.store, data: buckets[storeKey] ?? [], stack: metric.key });
      if (channels.includes('Online')) out.push({ id: `${metric.key}-Online`, metric, name: `${t(metric.label)} · ${t('Online')}`, unit: metric.unit, color: colors.online, data: buckets[onlineKey] ?? [], stack: metric.key });
    } else {
      // Channel-agnostic data (warehouse / finance): both buckets summed, unaffected by the channel filter
      out.push({ id: metric.key, metric, name: t(metric.label), unit: metric.unit, color: colors.store, data: addArrays(buckets[storeKey], buckets[onlineKey]), stack: null });
    }
  });
  return out;
};

/* ----------------------------------------------------------------------------
 * Chart.js lifecycle hook
 * ---------------------------------------------------------------------------*/

type ComboConfig = ChartConfiguration<'bar' | 'line', number[], string>;

const useChartJs = (config: ComboConfig | null) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart<'bar' | 'line', number[], string> | null>(null);

  useEffect(() => () => {
    chartRef.current?.destroy();
    chartRef.current = null;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!config) {
      chartRef.current?.destroy();
      chartRef.current = null;
      return;
    }
    if (chartRef.current) {
      chartRef.current.data = config.data;
      chartRef.current.options = config.options ?? {};
      chartRef.current.update();
    } else {
      chartRef.current = new Chart(canvas, config);
    }
  }, [config]);

  return canvasRef;
};

/* ----------------------------------------------------------------------------
 * The chart itself (no card) — also used by the legacy DashboardGraph exports
 * ---------------------------------------------------------------------------*/

export type DashboardComboChartProps = {
  period: Period;
  selectedBars: DashboardBarKey[];
  selectedSalesChannels: SalesChannel[];
  dataProvider: ChartDataProvider;
  minHeight?: number;
};

const useDashboardChartData = (period: Period, dataProvider: ChartDataProvider) => {
  const labels = useMemo(() => getRollingDashboardLabels(period), [period]);
  const displayLabels = useMemo(() => getDisplayLabels(period, labels), [period, labels]);
  const buckets = useMemo(() => {
    const req: ChartRequest = {
      period,
      selectedBars: DASHBOARD_METRICS.map(m => m.key),
      selectedChannels: ['Online', 'Store'],
      byRep: false,
      selectedReps: [],
      allReps: [],
      orientation: 'vertical',
    };
    return dataProvider.getStandardBuckets(req, labels) as Record<StandardSeriesKeys, number[]>;
  }, [dataProvider, labels, period]);
  return { labels, displayLabels, buckets };
};

export const DashboardComboChart = ({ period, selectedBars, selectedSalesChannels, dataProvider, minHeight = 360 }: DashboardComboChartProps) => {
  const { t } = useTranslation();
  const palette = useDashboardColors();
  const ct = useDashboardChartTheme();
  const { displayLabels, buckets } = useDashboardChartData(period, dataProvider);

  const series = useMemo(
    () => buildSeries(selectedBars, buckets, selectedSalesChannels, palette, t),
    [selectedBars, buckets, selectedSalesChannels, palette, t],
  );

  const config = useMemo<ComboConfig | null>(() => {
    if (!series.length) return null;
    const hasKr = series.some(s => s.unit === 'kr');
    const hasSt = series.some(s => s.unit === 'st');
    const units = series.map(s => s.unit);

    const datasets = series.map(s => (s.unit === 'kr'
      ? {
        type: 'bar' as const,
        label: s.name,
        data: s.data,
        yAxisID: 'kr',
        stack: s.stack ?? s.id,
        backgroundColor: s.color,
        borderColor: ct.surface,
        borderWidth: s.stack ? { top: 1 } : 0,
        borderSkipped: 'start' as const,
        borderRadius: 3,
        maxBarThickness: 30,
        order: 2,
      }
      : {
        type: 'line' as const,
        label: s.name,
        data: s.data,
        yAxisID: 'st',
        borderColor: s.color,
        backgroundColor: s.color,
        borderWidth: 2.5,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBorderColor: ct.surface,
        pointBorderWidth: 2,
        cubicInterpolationMode: 'monotone' as const,
        order: 0,
      })) as ChartDataset<'bar' | 'line', number[]>[];

    const tickFont = { family: ct.fontFamily, size: 11 };
    const titleFont = { family: ct.fontFamily, size: 11, weight: 600 as const };
    const scales: ChartOptions<'bar' | 'line'>['scales'] = {
      x: {
        stacked: true,
        grid: { display: false },
        border: { color: ct.axis },
        ticks: {
          color: ct.textSecondary,
          font: tickFont,
          maxRotation: 0,
          autoSkip: true,
          callback(this: Scale, value: string | number) {
            const label = this.getLabelForValue(Number(value));
            return this.chart.width < 520 && period === 'Week' ? label.split(' ')[0] : label;
          },
        },
      },
    };
    if (hasKr) {
      scales.kr = {
        type: 'linear',
        position: 'left',
        stacked: true,
        beginAtZero: true,
        grid: { color: ct.grid },
        border: { display: false },
        ticks: { color: ct.textSecondary, font: tickFont, maxTicksLimit: 6, callback: v => fmtAxis(Number(v)) },
        title: { display: true, text: 'kr', color: ct.textSecondary, font: titleFont },
      };
    }
    if (hasSt) {
      scales.st = {
        type: 'linear',
        position: hasKr ? 'right' : 'left',
        stacked: false,
        beginAtZero: true,
        grid: { display: !hasKr, color: ct.grid },
        border: { display: false },
        ticks: { color: ct.textSecondary, font: tickFont, maxTicksLimit: 6, precision: 0 },
        title: { display: true, text: 'st', color: ct.textSecondary, font: titleFont },
      };
    }

    const options: ChartOptions<'bar' | 'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 250 },
      // don't sweep new series in from the left when the selection changes
      animations: { x: { duration: 0 } },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        tooltip: {
          backgroundColor: ct.surface,
          borderColor: ct.border,
          borderWidth: 1,
          titleColor: ct.text,
          bodyColor: ct.textSecondary,
          padding: 10,
          boxPadding: 4,
          cornerRadius: 10,
          usePointStyle: true,
          titleFont: { family: ct.fontFamily, weight: 700 },
          bodyFont: { family: ct.fontFamily },
          // value (kr) rows first, then quantities (st)
          itemSort: (a, b) => (units[a.datasetIndex] === units[b.datasetIndex] ? 0 : units[a.datasetIndex] === 'kr' ? -1 : 1),
          callbacks: {
            label: ctx => ` ${ctx.dataset.label}: ${fmtUnit(Number(ctx.parsed.y ?? 0), units[ctx.datasetIndex])}`,
          },
        },
      },
      scales,
    };

    return { type: 'bar', data: { labels: displayLabels, datasets }, options } as ComboConfig;
  }, [series, ct, displayLabels, period]);

  const canvasRef = useChartJs(config);

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <canvas ref={canvasRef} role="img" aria-label={series.map(s => `${s.name}: ${fmtUnit(sumArray(s.data), s.unit)}`).join(', ')} />
      </Box>
      {!series.length && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 3 }}>
          <Typography variant="body2" color="text.secondary">{t('Nothing selected. Pick one or more data series above.')}</Typography>
        </Box>
      )}
    </Box>
  );
};

/* ----------------------------------------------------------------------------
 * Picker: presets (card header, top-right) + metric chips (under the title)
 * ---------------------------------------------------------------------------*/

const PresetBar = ({ selected, onChange }: { selected: DashboardBarKey[]; onChange: (next: DashboardBarKey[]) => void }) => {
  const { t } = useTranslation();
  const active = (Object.keys(DASHBOARD_METRIC_PRESETS) as DashboardMetricPreset[]).find(p => {
    const keys = DASHBOARD_METRIC_PRESETS[p];
    return keys.length === selected.length && keys.every(k => selected.includes(k));
  });
  const pill = (isActive: boolean) => ({
    fontSize: 12,
    lineHeight: 1.4,
    px: 1.25,
    py: 0.375,
    borderRadius: 999,
    border: 1,
    borderColor: isActive ? 'text.primary' : 'divider',
    bgcolor: isActive ? 'text.primary' : 'transparent',
    color: isActive ? 'background.paper' : 'text.secondary',
    fontWeight: isActive ? 600 : 500,
    '&:hover': { borderColor: 'text.secondary', color: isActive ? 'background.paper' : 'text.primary' },
  });

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', justifyContent: 'flex-end' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, mr: 0.25 }}>{t('Show')}</Typography>
      {(Object.keys(DASHBOARD_METRIC_PRESETS) as DashboardMetricPreset[]).map(p => (
        <ButtonBase key={p} aria-pressed={active === p} onClick={() => onChange([...DASHBOARD_METRIC_PRESETS[p]])} sx={pill(active === p)}>{t(p)}</ButtonBase>
      ))}
      <ButtonBase onClick={() => onChange([])} sx={pill(false)}>{t('Clear')}</ButtonBase>
    </Box>
  );
};

const Swatch = ({ metric, palette }: { metric: DashboardMetricDef; palette: DashboardPalette }) => {
  const c = metric.color(palette);
  const colors = metric.split ? [c.store, c.online] : [c.store];
  return (
    <Box sx={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>
      {colors.map((color, i) => (metric.unit === 'kr'
        ? <Box key={i} sx={{ width: 8, height: 14, borderRadius: '2px', bgcolor: color }} />
        : <Box key={i} sx={{ width: 14, height: 3, borderRadius: '2px', bgcolor: color }} />))}
    </Box>
  );
};

const MetricChips = ({ selected, onChange, buckets, channels }: {
  selected: DashboardBarKey[];
  onChange: (next: DashboardBarKey[]) => void;
  buckets: Record<StandardSeriesKeys, number[]>;
  channels: SalesChannel[];
}) => {
  const { t } = useTranslation();
  const palette = useDashboardColors();

  const totalFor = (m: DashboardMetricDef) => {
    const [storeKey, onlineKey] = m.buckets;
    if (!m.split) return sumArray(buckets[storeKey] ?? []) + sumArray(buckets[onlineKey] ?? []);
    return (channels.includes('Store') ? sumArray(buckets[storeKey] ?? []) : 0) + (channels.includes('Online') ? sumArray(buckets[onlineKey] ?? []) : 0);
  };
  const toggle = (key: DashboardBarKey) => onChange(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
        {METRIC_GROUPS.map(group => (
          <Box key={group} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, mr: 0.25, width: { xs: '100%', sm: 'auto' } }}>{t(group)}</Typography>
            {DASHBOARD_METRICS.filter(m => m.group === group).map(m => {
              const on = selected.includes(m.key);
              return (
                <ButtonBase
                  key={m.key}
                  aria-pressed={on}
                  onClick={() => toggle(m.key)}
                  sx={{
                    gap: 0.875,
                    px: 1.125,
                    py: 0.625,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    fontSize: 12.5,
                    whiteSpace: 'nowrap',
                    color: 'text.secondary',
                    bgcolor: on ? (theme) => alpha(theme.palette.text.primary, 0.06) : 'transparent',
                    opacity: on ? 1 : 0.6,
                    '& .swatch': { filter: on ? 'none' : 'grayscale(1)', opacity: on ? 1 : 0.5 },
                    '&:hover': { opacity: 1, borderColor: 'text.secondary' },
                  }}
                >
                  <span className="swatch" style={{ display: 'inline-flex' }}><Swatch metric={m} palette={palette} /></span>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{t(m.label)}</Box>
                  <Box component="span" sx={{ fontSize: 11 }}>{fmtUnit(totalFor(m), m.unit)}</Box>
                </ButtonBase>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ----------------------------------------------------------------------------
 * Chart card
 * ---------------------------------------------------------------------------*/

export type DashboardChartCardProps = {
  period: Period;
  selectedBars: DashboardBarKey[];
  /** When omitted the card keeps its own selection state. */
  onSelectedBarsChange?: (next: DashboardBarKey[]) => void;
  selectedSalesChannels: SalesChannel[];
  dataProvider: ChartDataProvider;
  rangeTitle?: string;
};

export const DashboardChartCard = ({ period, selectedBars, onSelectedBarsChange, selectedSalesChannels, dataProvider, rangeTitle }: DashboardChartCardProps) => {
  const { t } = useTranslation();
  const [internal, setInternal] = useState<DashboardBarKey[]>(selectedBars);
  const selected = onSelectedBarsChange ? selectedBars : internal;
  const setSelected = onSelectedBarsChange ?? setInternal;
  const { buckets } = useDashboardChartData(period, dataProvider);

  return (
    <DashboardCard
      area="chart"
      id="order-chart"
      title={rangeTitle ? `${t('Orders Overview')} — ${rangeTitle}` : t('Orders Overview')}
      action={<PresetBar selected={selected} onChange={setSelected} />}
      toolbar={<MetricChips selected={selected} onChange={setSelected} buckets={buckets} channels={selectedSalesChannels} />}
    >
      <DashboardComboChart period={period} selectedBars={selected} selectedSalesChannels={selectedSalesChannels} dataProvider={dataProvider} />
    </DashboardCard>
  );
};

/* ----------------------------------------------------------------------------
 * Legacy exports (same names/props as before) so other imports keep working
 * ---------------------------------------------------------------------------*/

export type DashboardGraphProps = {
  period: Period;
  selectedBars: DashboardBarKey[];
  selectedSalesChannels: SalesChannel[];
  viewBySalesRep?: boolean;
  selectedSalesReps?: string[];
  salesRepsData?: SalesRep[];
  dataProvider: ChartDataProvider;
  height?: number;
};

export const DashboardGraph = ({ height = 520, period, selectedBars, selectedSalesChannels, dataProvider }: DashboardGraphProps) => (
  <Box sx={{ height, display: 'flex', flexDirection: 'column' }}>
    <DashboardComboChart period={period} selectedBars={selectedBars} selectedSalesChannels={selectedSalesChannels} dataProvider={dataProvider} minHeight={height} />
  </Box>
);

export const DashboardGraphMobile = (props: DashboardGraphProps) => <DashboardGraph {...props} height={props.height ?? 420} />;

export type DashboardBarKey = 'ordersBar' | 'ordersValueBar' | 'purchasesValueBar' | 'toShipBar' | 'shippedBar' | 'returnsBar' | 'creditNotesBar' | 'creditNotesValueBar';

export type StandardSeriesKeys = 'ordersStore' | 'ordersOnline' | 'ordersValueStore' | 'ordersValueOnline' | 'purchasesValueStore' | 'purchasesValueOnline' | 'toShipStore' | 'toShipOnline' | 'shippedStore' | 'shippedOnline' | 'returnsStore' | 'returnsOnline' | 'creditNotesStore' | 'creditNotesOnline' | 'creditNotesValueStore' | 'creditNotesValueOnline';

export type ChartRequest = {
  period: Period;
  selectedBars: DashboardBarKey[];
  selectedChannels: SalesChannel[];
  byRep: boolean;
  selectedReps: string[];
  allReps: SalesRep[];
  orientation: 'vertical' | 'horizontal';
};

export interface ChartDataProvider {
  getSalesRepSeries(req: ChartRequest, labels: string[]): Record<string, number[]>;
  getStandardBuckets(req: ChartRequest, labels: string[]): Record<StandardSeriesKeys, number[]>;
}
